import React from 'react';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Space } from '@components/atoms/Space';
import { useTranslation } from 'react-i18next';
import { Container } from '@components/atoms/Container';
import styles from './UserArea.module.css';
import { RoundProfilePicture } from '@components/atoms/RoundProfilePicture';
import { RoundIcon, RoundIconVariants } from '@components/atoms/RoundIcon';
import { Accordion, AccordionVariants } from '@components/atoms/Accordion';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { SITE_URLS } from '@constants';
import { useNavigate } from 'react-router-dom';
import { LogoutForm } from '@components/molecules/LogoutForm';
import { DeleteAccountForm } from '@components/molecules/DeleteAccountForm';
import { ChangeUnit } from '@components/molecules/ChangeUnit';
import { ChangeLanguage } from '@components/molecules/ChangeLanguage';
import { ChangePrimaryVehicle } from '@components/molecules/ChangePrimaryVehicle';
import { ToggleSwitch } from '@components/atoms/ToggleSwitch';
import { useRoleChecker } from '@hooks/useRoleChecker';
import { TwoFactorAuth } from '../TwoFactorAuth';
import { useToggleShareLocation } from '@services/useToggleShareLocation';
import { useNotifications } from '@hooks/useNotifications';
import { useData } from '@hooks/useData';

interface UserAreaProps {
    userData: {
        name: string;
        email: string;
        user_type: string;
        profile_photo_url?: string | null;
    };
}

export function UserArea({ userData }: UserAreaProps) {
    const { t } = useTranslation();
    const { isDriver } = useRoleChecker();
    const navigate = useNavigate();

    const { user, lastLocationSaved } = useData();
    const { request: requestShareLocation } = useToggleShareLocation();
    const { showDefaultNotification } = useNotifications();

    const isAvailable = !!user?.data?.share_location;

    const translations = {
        personal: t('user-area.personal'),
        label: t('user-area.legal-info.label'),
        terms: t('user-area.legal-info.terms'),
        privacy: t('user-area.legal-info.privacy'),
        car: t('user-area.car'),
        state: t('user-area.state'),
        delete: t('user-area.delete'),
    };

    const handleAvailabilityToggle = async (checked: boolean) => {
        if (!checked) {
            await requestShareLocation({ share_location: false });
            return;
        }

        if (!lastLocationSaved?.coordinates) {
            showDefaultNotification({
                type: 'error',
                message: t('availability-toggle.missing-coordenates'),
            });
            return;
        }

        try {
            await requestShareLocation({
                share_location: true,
                latitude: lastLocationSaved.coordinates[0],
                longitude: lastLocationSaved.coordinates[1],
            });
        } catch {
            showDefaultNotification({
                type: 'error',
                message: t('availability-toggle.error-sharing-location'),
            });
        }
    };

    return (
        <Container classes={styles.containerBackground}>
            <div className="d-flex position-relative">
                <div className="position-absolute start-0">
                    <RoundIcon
                        classes={styles.roundIcon}
                        icon="fa-thin fa-arrow-left"
                        variant={RoundIconVariants.variant1}
                        onClick={() => navigate('/map')}
                    />
                </div>

                <div className="d-flex justify-content-center m-auto">
                    <RoundProfilePicture
                        src={userData.profile_photo_url ?? undefined}
                        icon="fa-thin fa-user-tie-hair"
                        labelText={userData.name}
                    />
                </div>
            </div>

            <hr />

            <>
                {isDriver && (
                    <>
                        <div className="d-flex justify-content-between align-items-center">
                            <Typography fontSize="16" fontWeight="300">
                                <>
                                    <Typography fontWeight="300" tagVariant={TypographyTagVariants.span}>
                                        <i style={{ marginRight: '5px' }} className="fa-light fa-circle-exclamation"></i>
                                    </Typography>
                                    {` ${translations.state}`}
                                </>
                            </Typography>

                            <ToggleSwitch id="availability-toggle" checked={isAvailable} onChange={handleAvailabilityToggle} />
                        </div>

                        <Space spaceBreakpoints={{ xs: { space: 30 } }} />
                    </>
                )}
            </>

            <Anchor href={SITE_URLS.editProfile} mode={AnchorMode.none}>
                <Typography fontSize="16" fontWeight="300" color="dark">
                    <>
                        <Typography fontWeight="300" tagVariant={TypographyTagVariants.span}>
                            <i style={{ marginRight: '5px' }} className="fa-thin fa-user-hair"></i>
                        </Typography>
                        {` ${translations.personal}`}
                    </>
                </Typography>
            </Anchor>

            <ChangeUnit />

            <ChangePrimaryVehicle />

            <>
                {isDriver && (
                    <>
                        <Space spaceBreakpoints={{ xs: { space: 30 } }} />
                        <Anchor href={SITE_URLS.myVehicles} mode={AnchorMode.none}>
                            <Typography fontSize="16" fontWeight="300" color="dark">
                                <>
                                    <Typography fontWeight="300" tagVariant={TypographyTagVariants.span}>
                                        <i style={{ marginRight: '5px' }} className="fa-thin fa-cars"></i>
                                    </Typography>
                                    {` ${translations.car}`}
                                </>
                            </Typography>
                        </Anchor>
                    </>
                )}
            </>

            <ChangeLanguage />

            <>{user?.data.email ? <TwoFactorAuth /> : null}</>

            <Space spaceBreakpoints={{ xs: { space: 30 } }} />

            <Accordion
                variant={AccordionVariants.variant1}
                items={[
                    {
                        bodyContent: [
                            <Anchor href={SITE_URLS.termsConditions} mode={AnchorMode.none} key="1">
                                <Typography fontSize="16" fontWeight="300" color="dark">
                                    {translations.terms}
                                </Typography>
                            </Anchor>,
                            <Anchor href={SITE_URLS.privacyPolicy} mode={AnchorMode.none} key="2">
                                <Typography fontSize="16" fontWeight="300" color="dark">
                                    {translations.privacy}
                                </Typography>
                            </Anchor>,
                        ],
                        buttonContent: (
                            <Typography fontSize="16" fontWeight="300">
                                <>
                                    <Typography fontWeight="300" tagVariant={TypographyTagVariants.span}>
                                        <i style={{ marginRight: '5px' }} className="fa-light fa-circle-info"></i>
                                    </Typography>
                                    {` ${translations.label}`}
                                </>
                            </Typography>
                        ),
                        id: 'accordion-variant-1-step1',
                    },
                ]}
            />

            <Space spaceBreakpoints={{ xs: { space: 15 } }} />

            <LogoutForm />

            <Space spaceBreakpoints={{ xs: { space: 30 } }} />

            <DeleteAccountForm />
        </Container>
    );
}
